import {
  ColorRank,
  RadarSearchDto,
  RadarSearchMode,
  RadarStatus,
} from "../types/battle"
import { RANK_ORDER, RADAR_PTS_MATCH_RANGE } from "../constants/game.constants"

const COLOR_RANKS: ColorRank[] = [...RANK_ORDER]
const SEARCH_MODES: RadarSearchMode[] = ["RATING", "RANK"]

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value
  if (value != null) return String(value)
  return fallback
}

function pickEnum<T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | undefined {
  const raw = pickString(value).toUpperCase()
  return allowed.find((item) => item === raw)
}

export function normalizeColorRank(value: unknown): ColorRank | undefined {
  return pickEnum(value, COLOR_RANKS)
}

export function normalizeRadarSearchMode(
  value: unknown
): RadarSearchMode | undefined {
  return pickEnum(value, SEARCH_MODES)
}

export function normalizeAvailableRanks(value: unknown): ColorRank[] | undefined {
  if (!Array.isArray(value)) return undefined
  const ranks = value
    .map((item) => normalizeColorRank(item))
    .filter((item): item is ColorRank => item != null)
  return ranks.length ? ranks : undefined
}

export function buildRadarSearchDto(
  params?: RadarSearchDto
): RadarSearchDto | undefined {
  if (!params) return undefined
  const searchMode = params.searchMode ?? "RATING"
  if (searchMode === "RATING") {
    return { searchMode: "RATING" }
  }
  if (!params.colorRank) {
    return { searchMode: "RANK" }
  }
  return { searchMode: "RANK", colorRank: params.colorRank }
}

export function radarStatusQueryParams(
  params?: RadarSearchDto
): Record<string, string> | undefined {
  const dto = buildRadarSearchDto(params)
  if (!dto) return undefined
  const query: Record<string, string> = { searchMode: dto.searchMode ?? "RATING" }
  if (dto.searchMode === "RANK" && dto.colorRank) {
    query.colorRank = dto.colorRank
  }
  return query
}

export function normalizeRadarStatus(data: unknown): RadarStatus {
  const obj =
    data && typeof data === "object" && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : {}

  const searchMode = normalizeRadarSearchMode(
    obj.searchMode ?? obj.search_mode
  )
  const colorRank = normalizeColorRank(obj.colorRank ?? obj.color_rank)
  const availableRanks = normalizeAvailableRanks(
    obj.availableRanks ?? obj.available_ranks
  )

  return {
    queueSize: pickNumber(obj.queueSize ?? obj.queue_size),
    matchableCount: pickNumber(obj.matchableCount ?? obj.matchable_count),
    ptsMatchRange: pickNumber(
      obj.ptsMatchRange ?? obj.pts_match_range,
      RADAR_PTS_MATCH_RANGE
    ),
    viewerGlobalPts: (() => {
      const raw = obj.viewerGlobalPts ?? obj.viewer_global_pts ?? obj.globalPts ?? obj.global_pts
      if (raw == null || raw === "") return null
      return pickNumber(raw, NaN) || null
    })(),
    availableRanks,
    searchMode,
    colorRank,
    message: pickString(obj.message) || undefined,
  }
}

export function formatRadarQueueLine(status: RadarStatus): string {
  return `В поиске: ${status.queueSize} · подходящих: ${status.matchableCount}`
}
